import React, { useRef, useState } from 'react';
import {
  BorderOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  NOTIFICATION_FIELD_LABELS,
  Notification,
  NotificationMetadata,
  NotificationType,
} from '../../../types/Notification';
import { ActivityValueSnapshot } from '../../../types/TaskActivity';
import { getUserProfilePictureUrl } from '../../../common/userHelper';
import { extractTextFromHtml } from '../../../utils/textUtils';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import { valuePill } from '../../../pages/Task/components/activity/activityRenderer';

// Small icon button for the hover action bar (matches the InboxMenu trigger style).
const HOVER_BTN =
  'flex items-center justify-center w-6 h-6 rounded cursor-pointer hover:bg-neutral-200 text-neutral-900';

// Sentence for a property change, e.g. "changed the Owner in". Labels are the one
// property the design words differently, and an unrecognised field degrades to a
// generic sentence rather than printing a raw column name at the reader.
const propertyAction = (metadata: NotificationMetadata | null): string => {
  const field = metadata?.field;
  if (field === 'labelId') return 'updated the Labels in';
  const label = field ? NOTIFICATION_FIELD_LABELS[field] : undefined;
  return label ? `changed the ${label} in` : 'updated';
};

const getAction = (notification: Notification): string => {
  switch (notification.type) {
    case NotificationType.mention:
      return 'mentioned you in comment of';
    case NotificationType.comment:
      return 'left a comment in';
    case NotificationType.assigned_task:
      return 'assigned you';
    case NotificationType.deadline:
      // Preceded by the "Today" label that stands in for an actor (see below).
      return 'is the deadline for';
    case NotificationType.invited_to_space:
      return 'invited you to';
    case NotificationType.user_added_to_space:
      return 'added you to';
    case NotificationType.deleted_task:
      return 'deleted the task assigned to you:';
    case NotificationType.task_property_changed:
      return propertyAction(notification.metadata);
    case NotificationType.description_updated:
      return 'updated the description in';
    case NotificationType.attachment_added:
      return 'added attachment in';
    case NotificationType.link_added:
      return 'added link in';
    case NotificationType.comment_reaction:
      return 'reacted to your comment in';
    case NotificationType.description_mention:
      return 'mentioned you in description of';
    default:
      return '';
  }
};

// A property change with no snapshot on either side still needs a from/to row —
// the design prints "None" where a value is absent (e.g. Blocking: None → Story 1).
const NoValue: React.FC = () => <span className="text-sm text-neutral-800">None</span>;

/**
 * The Old:/New: block under a property-change card. The snapshots come straight
 * from the server, so the same pills the activity log draws are reused verbatim
 * rather than reimplemented here.
 */
// The pills cap their own width for the task view (max-w-[180px]), which is
// wider than the ~156px a sidebar card leaves once the label column is taken
// out — so the cell caps its child instead of letting a long value spill past
// the card edge. The pill's own truncation then does the work.
const VALUE_CELL = 'min-w-0 overflow-hidden [&>*]:max-w-full';

/**
 * One Old:/New: row. The chips returned by `valuePill()` gate their own tooltip on
 * their own overflow, so this cell only speaks for the values that are NOT chips
 * (dates, "None") and would otherwise be cut off by the cell with nothing to
 * reveal them. Gated on a real measurement so a value that fits shows no tooltip
 * at all — a tooltip repeating a fully visible value is the bug in #3002.
 */
const ValueRow: React.FC<{
  label: string;
  labelClass: string;
  cellClass?: string;
  snap?: ActivityValueSnapshot | null;
  field: string | null;
}> = ({ label, labelClass, cellClass = '', snap, field }) => {
  const cellRef = useRef<HTMLSpanElement>(null);
  const [isClipped, setIsClipped] = useState(false);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className={`w-8 shrink-0 text-[10px] font-medium leading-3 ${labelClass}`}>{label}</span>
      <CustomTooltip title={isClipped ? (snap?.display ?? '') : ''}>
        <span
          ref={cellRef}
          className={`${VALUE_CELL} ${cellClass}`}
          onMouseEnter={() => {
            if (cellRef.current) setIsClipped(cellRef.current.scrollWidth > cellRef.current.clientWidth);
          }}
        >
          {valuePill(field, snap ?? null) ?? <NoValue />}
        </span>
      </CustomTooltip>
    </div>
  );
};

const PropertyChange: React.FC<{ metadata: NotificationMetadata }> = ({ metadata }) => {
  const field = metadata.field ?? null;
  // Labels and any snapshot-less change are a sentence on their own.
  if (!metadata.oldValue && !metadata.newValue) return null;
  return (
    <div className="pl-5 pr-1 flex flex-col gap-2">
      <ValueRow
        label="Old:"
        labelClass="text-neutral-800"
        cellClass="opacity-50"
        snap={metadata.oldValue}
        field={field}
      />
      <ValueRow label="New:" labelClass="text-blue-600" snap={metadata.newValue} field={field} />
    </div>
  );
};

const relativeDate = (d: Date | string): string =>
  new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// The stored message is Tiptap HTML; Figma renders it as clean text with @mentions
// highlighted orange (#d97008). Replace mention spans with a sentinel, strip the
// remaining tags, then split the sentinel back out to colour the mentions.
const MENTION_MARK = '';
const markMentions = (message: string): string =>
  extractTextFromHtml(
    message.replace(/<span[^>]*data-type="mention"[^>]*>([^<]+)<\/span>/g, `${MENTION_MARK}@$1${MENTION_MARK}`)
  );

// The same text the card previews, mention marks dropped — the tooltip shows it in full (#3002).
const previewText = (message: string): string => markMentions(message).split(MENTION_MARK).join('');

const renderPreview = (message: string): React.ReactNode => {
  const text = markMentions(message);
  return text.split(MENTION_MARK).map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} className="font-semibold text-[#d97008]">
        {part}
      </span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

// The two types whose subject is a space rather than a task.
const isSpaceNotification = (notification: Notification): boolean =>
  notification.type === NotificationType.invited_to_space || notification.type === NotificationType.user_added_to_space;

/**
 * Who the card names as the actor. A creator with no `fullName` yet (magic-link
 * signups never set one) is still a known person, so fall back to their email
 * before giving up — printing "Someone" over an identified inviter is the #2995
 * bug. `Creator` itself is only null once that account is deleted: the relation
 * is `onDelete: SetNull`, so the name is genuinely unrecoverable and the card
 * says so rather than inventing an actor.
 */
const actorNameOf = (creator: Notification['Creator']): string => {
  if (!creator) return 'A deleted user';
  return creator.fullName?.trim() || creator.email?.split('@')[0] || 'A deleted user';
};

const initials = (name?: string): string =>
  (name ?? '')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

export interface NotificationCardProps {
  notification: Notification;
  selected: boolean;
  showNewDot: boolean;
  // Tailwind background class for the "new" dot — blue-600 on the Actions tab, orange-500 on Updates.
  newDotColor?: string;
  onClick: () => void;
  onDone?: () => void;
  onToggleRead?: () => void;
  onDelete?: () => void;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  selected,
  showNewDot,
  newDotColor = 'bg-orange-500',
  onClick,
  onDone,
  onToggleRead,
  onDelete,
}) => {
  // Deadline notifications are raised by the cron with no creator (#2427) — there is no actor to
  // name, so the card shows the deadline icon alone rather than inventing one.
  const isSystem = notification.type === NotificationType.deadline;
  const actorName = actorNameOf(notification.Creator);
  // Tooltips fire only when the underlying text is actually clipped (#3002 bounce-back) —
  // measured on hover rather than assumed, since whether a given name/title/comment
  // truncates depends on its own length against the card's fixed width.
  const [isActorNameTruncated, setIsActorNameTruncated] = useState(false);
  const actorNameRef = useRef<HTMLSpanElement>(null);
  const [isSubjectTitleTruncated, setIsSubjectTitleTruncated] = useState(false);
  const subjectTitleRef = useRef<HTMLSpanElement>(null);
  const [isMessageTruncated, setIsMessageTruncated] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);
  // Space notifications carry a Workspace instead of a Task, so "invited you to"
  // had nothing to name after it until the space title was rendered too (#2995).
  const subjectTitle =
    notification.Task?.title ?? (isSpaceNotification(notification) ? notification.Workspace?.name : undefined);
  const avatarSrc = notification.Creator?.profilePicturePath
    ? getUserProfilePictureUrl(notification.Creator.profilePicturePath)
    : undefined;

  // Selected uses an outline, not a wider border, so toggling it never changes
  // the box size (border stays 1px) and the card doesn't jump.
  return (
    <div
      onClick={onClick}
      className={`group/card relative flex flex-col gap-2 p-2 rounded-lg cursor-pointer bg-brand-white border border-solid ${
        selected
          ? 'border-transparent outline outline-2 outline-taskonioGreen-300'
          : 'border-neutral-200 hover:bg-neutral-100'
      }`}
    >
      <div className="flex flex-col gap-1">
        {/* Name + Date */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 min-w-0">
            {isSystem ? (
              <>
                <span
                  data-testid="notification-deadline-icon"
                  className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center bg-[#ff9c8f] text-[8px] text-neutral-1000"
                >
                  <ClockCircleOutlined />
                </span>
                <span className="text-xs font-semibold text-neutral-1000 leading-4 truncate">Today</span>
              </>
            ) : (
              <>
                <span className="shrink-0 w-4 h-4 rounded-full overflow-hidden flex items-center justify-center bg-neutral-300 text-[8px] font-semibold text-neutral-1000">
                  {avatarSrc ? (
                    <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
                  ) : (
                    initials(actorName)
                  )}
                </span>
                <CustomTooltip title={isActorNameTruncated ? actorName : ''}>
                  <span
                    ref={actorNameRef}
                    className="text-xs font-semibold text-neutral-1000 leading-4 truncate"
                    onMouseEnter={() => {
                      if (actorNameRef.current) {
                        setIsActorNameTruncated(actorNameRef.current.scrollWidth > actorNameRef.current.clientWidth);
                      }
                    }}
                  >
                    {actorName}
                  </span>
                </CustomTooltip>
              </>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-neutral-1000 leading-4 whitespace-nowrap">
              {relativeDate(notification.createdAt)}
            </span>
            {showNewDot && (
              <span data-testid="notification-new-dot" className={`w-1.5 h-1.5 rounded-full ${newDotColor}`} />
            )}
          </div>
        </div>
        {/* Action verb, indented 20px to align under the name (past the 16px avatar + gap) */}
        <div className="pl-5 text-xs text-neutral-1000 leading-4">{getAction(notification)}</div>
        {/* Task title, or the space name for space notifications */}
        {subjectTitle && (
          <div className="pl-5 pr-1">
            <CustomTooltip title={isSubjectTitleTruncated ? subjectTitle : ''}>
              <span
                ref={subjectTitleRef}
                className="text-xs font-semibold text-neutral-1000 underline leading-4 line-clamp-1"
                onMouseEnter={() => {
                  if (subjectTitleRef.current) {
                    setIsSubjectTitleTruncated(
                      subjectTitleRef.current.scrollHeight > subjectTitleRef.current.clientHeight
                    );
                  }
                }}
              >
                {subjectTitle}
              </span>
            </CustomTooltip>
          </div>
        )}
      </div>
      {/* Reason / comment preview */}
      {notification.message && (
        // Five lines is where the design cuts the comment off (every comment card in
        // Figma runs to 5×16px and ends in an ellipsis); the rest is on the tooltip (#3013).
        <CustomTooltip title={isMessageTruncated ? previewText(notification.message) : ''}>
          <div
            ref={messageRef}
            className="pl-5 pr-1 text-xs text-neutral-900 leading-4 break-words line-clamp-5"
            onMouseEnter={() => {
              if (messageRef.current) {
                setIsMessageTruncated(messageRef.current.scrollHeight > messageRef.current.clientHeight);
              }
            }}
          >
            {renderPreview(notification.message)}
          </div>
        </CustomTooltip>
      )}
      {/* Old:/New: values for a property change */}
      {notification.type === NotificationType.task_property_changed && notification.metadata && (
        <PropertyChange metadata={notification.metadata} />
      )}
      {/* The emoji someone reacted with, under the comment they reacted to */}
      {notification.type === NotificationType.comment_reaction && notification.metadata?.emoji && (
        <div className="pl-5 pr-1">
          <span
            data-testid="notification-reaction-emoji"
            className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-solid border-neutral-200 bg-brand-white text-xs text-neutral-1000"
          >
            <span>{notification.metadata.emoji}</span>
            <span>1</span>
          </span>
        </div>
      )}
      {/* Hover action bar (like the old notification list): read/unread + delete,
          plus mark-as-done on the Actions tab. Revealed on card hover. */}
      {(onToggleRead || onDone || onDelete) && (
        <div className="absolute top-1 right-1 hidden group-hover/card:flex items-center gap-0.5 rounded-md border border-solid border-neutral-200 bg-brand-white p-0.5 shadow-md">
          {onToggleRead && (
            <CustomTooltip title={notification.read ? 'Mark as unread' : 'Mark as read'}>
              <span
                role="button"
                aria-label={notification.read ? 'Mark as unread' : 'Mark as read'}
                className={HOVER_BTN}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  onToggleRead();
                }}
              >
                {notification.read ? <BorderOutlined /> : <CheckSquareOutlined />}
              </span>
            </CustomTooltip>
          )}
          {onDone && (
            // Toggles both ways, like the read/unread control above it: a completed
            // action was otherwise stuck completed with no way back (#2868).
            <CustomTooltip title={notification.done ? 'Mark as To do' : 'Mark as done'}>
              <span
                role="button"
                aria-label={notification.done ? 'Mark as To do' : 'Mark as done'}
                className={HOVER_BTN}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  onDone();
                }}
              >
                {notification.done ? <BorderOutlined /> : <CheckCircleOutlined />}
              </span>
            </CustomTooltip>
          )}
          {onDelete && (
            <CustomTooltip title="Delete Notification">
              <span
                role="button"
                aria-label="Delete Notification"
                className={HOVER_BTN}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  onDelete();
                }}
              >
                <DeleteOutlined />
              </span>
            </CustomTooltip>
          )}
        </div>
      )}
    </div>
  );
};
